import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Call backend API
      const response = await axios.post(`${API}/contact`, formData);
      
      // Show success message
      toast({
        title: 'Message Sent!',
        description: response.data.message
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" style={{ background: 'var(--bg-section)', padding: '7rem 1.5rem 5rem' }}>
      <div className="container" style={{ maxWidth: '900px' }}>
        {/* CTA Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Let's Build Your Digital Future Together
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Ready to transform your online presence? Get in touch with us today.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem' }}>
          {/* Contact Form */}
          <div>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Send Us a Message
            </h3>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label htmlFor="name" className="body-medium" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'system-ui, sans-serif',
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
                />
              </div>

              <div>
                <label htmlFor="email" className="body-medium" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'system-ui, sans-serif',
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
                />
              </div>

              <div>
                <label htmlFor="phone" className="body-medium" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'system-ui, sans-serif',
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
                />
              </div>

              <div>
                <label htmlFor="message" className="body-medium" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'system-ui, sans-serif',
                    resize: 'vertical',
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
                style={{ gap: '0.5rem', width: '100%' }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Contact Information
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <div className="body-medium" style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Email</div>
                <a
                  href="mailto:ibtecon@gmail.com"
                  style={{ color: 'var(--accent-text)', textDecoration: 'none' }}
                  className="body-medium"
                >
                  ibtecon@gmail.com
                </a>
              </div>

              <div>
                <div className="body-medium" style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Phone</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <a
                    href="tel:+14102455584"
                    style={{ color: 'var(--accent-text)', textDecoration: 'none' }}
                    className="body-medium"
                  >
                    (410) 245-5584
                  </a>
                  <a
                    href="tel:+16027368649"
                    style={{ color: 'var(--accent-text)', textDecoration: 'none' }}
                    className="body-medium"
                  >
                    (602) 736-8649
                  </a>
                </div>
              </div>

              <div>
                <div className="body-medium" style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Office Address</div>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Sacramento, CA 95835
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
};

export default ContactSection;